import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import Loading from '../../Loading'
import ManualLineBarGraph from '../../graph/ManualLineBarGraph';
import TimeRangeContainer from '../../utils/TimeRangeContainer'
import AxisSettingButton from '../../utils/AxisSettingButton'
import SaveSettingButton from './SaveSettingButton'
import {GraphContainer} from '../../graph/GraphContainer';
import { saveGraphSettings } from '../../graph/utils/storageUtils';

const LongTermFinancialResultsGraph = ({ graphDataAnnual, graphDataQuarter, accounts, onPeriodChange, labelState, setLabelState, selectedTimeRange, setSelectedTimeRange, selectedPeriod, onSaveClick }) => {
    const [selectedRange, setSelectedRange] = useState(selectedTimeRange); // 3Y, 5Y, 10Y, MAX
    const [width, setWidth] = useState(0); // 幅を管理する状態
    const [state, setState] = useState(labelState)
    const ref = useRef(null);
    const timeRanges = [
        { label: '3Y', value: '3Y' },
        { label: '5Y', value: '5Y' },
        { label: '10Y', value: '10Y' },
        { label: '20Y', value: '20Y' },
        { label: 'MAX', value: 'MAX' },
    ];

    const generateGraphId = (range) => `longterm_graph_${range}`;
    const RANGES = ['3Y', '5Y', '10Y', '20Y', 'MAX'];
    const filterDataByYears = (data, years) => {
        return data.map(series => ({
            ...series,
            x: series.x.slice(-years),
            y: series.y.slice(-years)
        }));
    };

    const filterQuarterlyDataByYears = (data, years) => {
        const currentYear = new Date().getFullYear(); // 現在の年を取得
        const startYear = currentYear - years + 1; // 抽出開始年を計算
    
        return data.map(series => {
            // 年ごとのフィルタリング
            const filteredIndices = series.x.reduce((indices, quarter, index) => {
                const year = parseInt(quarter.split(' ')[0], 10); // "YYYY Q?" のYYYY部分を取得
                if (year >= startYear) {
                    indices.push(index);
                }
                return indices;
            }, []);
    
            // フィルタリングされたインデックスを基に x と y を抽出
            return {
                ...series,
                x: filteredIndices.map(i => series.x[i]),
                y: filteredIndices.map(i => series.y[i])
            };
        });
    };
    
    const makeDisplayData = (data) => {
        if (!data || data.length === 0) return null;
        
        if (selectedPeriod === 'annual') {
            switch (selectedRange) {
                case '3Y': 
                        
                case '5Y': 
                    return filterDataByYears(data, 5);
                case '10Y': 
                    return filterDataByYears(data, 10);
                case '20Y': 
                    return filterDataByYears(data, 20);
                case 'MAX': default: return data;
            }
        } else {
            switch (selectedRange) {
                case '3Y': 
                  return filterQuarterlyDataByYears(data, 3);
                case '5Y': 
                    return filterQuarterlyDataByYears(data, 5);
                case '10Y': 
                    return filterQuarterlyDataByYears(data, 10);
                case '20Y': 
                    return filterQuarterlyDataByYears(data, 20);
                case 'MAX': 
                default: 
                    return data;
            }
        }
    };

    const handlePeriodChange = (period) => {
        // setSelectedPeriod(period);
        onPeriodChange(period);
    };

    // 現在のグラフIDを生成（メモ化）
    const currentGraphId = useMemo(
        () => generateGraphId(selectedRange),
        [selectedRange]
    );

    // すべてのグラフに設定を適用する処理
    const handleApplySettingsToAll = useCallback((newSettings) => {
        RANGES.forEach(range => {
        const id = generateGraphId(range);
        saveGraphSettings(id, newSettings);
        });
    }, []);
    
    // containerのサイズをグラフに渡すため 
    useEffect(() => {
        if (!ref.current) return;

        // ResizeObserverを定義
        const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
            if (entry.contentRect) {
                setWidth(entry.contentRect.width); // ref要素の幅を設定
            }
        }
        });

        // ref要素を監視
        observer.observe(ref.current);

        // クリーンアップ処理
        return () => {
        if (ref.current) {
            observer.unobserve(ref.current);
        }
        };
    }, []);

    
    if (selectedPeriod === 'annual' && !graphDataAnnual) {
        return <Loading />;
    } else if (selectedPeriod === 'annual' && graphDataAnnual.length === 0) {
        return <div></div>
    }

    if (selectedPeriod === 'quarterly' && !graphDataQuarter) {
        return <Loading />;
    } else if (selectedPeriod === 'quarterly' && graphDataQuarter.length === 0) {
        return <div></div>;
    }  
    
    const graphData = selectedPeriod === 'annual' ? graphDataAnnual : graphDataQuarter
    const setGraphTypes = () => {
        const hasLine = graphData.some((series) => series.y_unit === '%');
        
        const hasBar = graphData.some((series) => series.y_unit !== '%');
        return [
            ...(hasLine ? ['line'] : []),
            ...(hasBar ? ['bar'] : [])
        ];
    }
    const graphTypes = setGraphTypes()

    const handleTimeRangeSetting = (newRange) => {
        setSelectedRange(newRange);
        setSelectedTimeRange(newRange);
    }

    const handleLabelSettingClick = (newState) => {
        setState(newState);
        setLabelState(newState);
    }
    console.log(graphTypes)
    return (
        <div className='dashboard-row'>
            <div className="flex flex-wrap gap-2 items-center mb-4 " ref={ref}>
                <TimeRangeContainer timeRanges={timeRanges} handleTimeRangeSetting={handleTimeRangeSetting} selectedRange={selectedRange} />
                <AxisSettingButton handleLabelSettingClick={handleLabelSettingClick}  labelState={state} />
                <SaveSettingButton onSaveClick={onSaveClick} addClass=''/>
            </div>
            <div className='pt-2'>
                    {graphTypes.includes('line') && graphTypes.includes('bar') ? (
                        <ManualLineBarGraph 
                            data={makeDisplayData(graphData)} 
                            width={width}
                            convertValue={true} 
                            showLabel={state} 
                        />
                    ) : graphTypes.includes('line') ? (
                        <GraphContainer 
                            key={currentGraphId}
                            graphId={currentGraphId}
                            initialData={makeDisplayData(graphData.filter(series => series.graphType === 'line'))}
                        />
                    ):  graphTypes.includes('bar') ? (
                        <GraphContainer 
                            key={currentGraphId}
                            graphId={currentGraphId}
                            initialData={makeDisplayData(graphData.filter(series => series.graphType === 'bar'))}
                        />
                    ) : null}
            </div>
        </div>
    );
};

export default LongTermFinancialResultsGraph;
